"use client";

import {
  EMBEDDED_IN_APP_ATTRIBUTE,
  EMBEDDED_SESSION_TRANSFER_ATTRIBUTE,
} from "@/lib/embeddedInAppBootstrap";
import { createContext, use, useEffect, useState, type ReactNode } from "react";

type EmbeddedInAppState = {
  embeddedInApp: boolean;
  /** Embedded, and the app hands this web view sessions through /auth/sso. */
  sessionTransfer: boolean;
  /** False until the document marker has been read, after the hydration render. */
  resolved: boolean;
};

const EmbeddedInAppContext = createContext<EmbeddedInAppState>({
  embeddedInApp: false,
  sessionTransfer: false,
  resolved: true,
});

/**
 * Embedded mode is resolved in the browser only. The HTML is shared between
 * visitors through a cache, so the server never reads the cookie; the value
 * comes from the marker `src/lib/embeddedInAppBootstrap.ts` sets on the
 * document before paint, read once after mount. Until then the state reports
 * `resolved: false`, so an effect that must not fire with the wrong answer
 * can wait.
 *
 * Chrome that must not appear in the app's web view is hidden by CSS keyed on
 * that same marker, so nothing visible waits on this effect.
 */
export function EmbeddedInAppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<EmbeddedInAppState>({
    embeddedInApp: false,
    sessionTransfer: false,
    resolved: false,
  });

  useEffect(() => {
    const root = document.documentElement;
    const embeddedInApp = root.hasAttribute(EMBEDDED_IN_APP_ATTRIBUTE);
    setState({
      embeddedInApp,
      sessionTransfer:
        embeddedInApp && root.hasAttribute(EMBEDDED_SESSION_TRANSFER_ATTRIBUTE),
      resolved: true,
    });
  }, []);

  return (
    <EmbeddedInAppContext.Provider value={state}>
      {children}
    </EmbeddedInAppContext.Provider>
  );
}

export function useEmbeddedInApp(): EmbeddedInAppState {
  return use(EmbeddedInAppContext);
}

export function useIsEmbeddedInApp(): boolean {
  return useEmbeddedInApp().embeddedInApp;
}

/**
 * Render children only when Discover is NOT inside the Spond app web view.
 * The wrapper is hidden by CSS before hydration and the children are dropped
 * after it, so server-rendered chrome never flashes in the web view.
 */
export function HideWhenEmbeddedInApp({ children }: { children: ReactNode }) {
  if (useIsEmbeddedInApp()) return null;
  return <div className="contents hide-when-embedded-in-app">{children}</div>;
}
